import { useState, useEffect, useRef } from "react";
import { AnimateIn } from "@/hooks/useAnimations";

const stats = [
  { label: "Websites Starting At", value: 7, suffix: "K", prefix: "Rs " },
  { label: "Days Delivery", value: 7, suffix: "-14" },
  { label: "Customer Satisfaction", value: 100, suffix: "%" },
  { label: "Cities We Serve", value: 25, suffix: "+" },
];

function AnimatedCounter({ target, suffix }) {
  const [count, setCount] = useState(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const duration = 2000;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [visible, target]);

  return <span ref={ref}>{count}{suffix}</span>;
}

export default function StatsSection() {
  return (
    <section data-testid="stats-section" className="py-20 bg-red-600 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -top-20 -left-20 w-64 h-64 border border-white rounded-full animate-[spin_20s_linear_infinite]" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 border border-white rounded-full animate-[spin_30s_linear_infinite_reverse]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] border border-white/30 rounded-full animate-pulse" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {stats.map((stat, i) => (
            <AnimateIn key={stat.label} delay={0.18 * i} direction="scale">
              <div className="text-center group cursor-pointer relative" data-testid={`stat-${i}`}>
                <p className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter group-hover:scale-110 group-hover:-translate-y-1 transition-transform duration-300 inline-block">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                </p>
                <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.15em] text-white/80 mt-2 group-hover:text-white transition-colors">
                  {stat.label}
                </p>
                <div className="mt-3 h-0.5 w-0 bg-white mx-auto group-hover:w-12 transition-[width] duration-500" />
              </div>
            </AnimateIn>
          ))}
        </div>
      </div>
    </section>
  );
}
